export const PAGE_TEMPLATES = [
  {
    name: "Home",
    template: `{% theme %}
{% title "Home" %}
{% favicon "favicon.svg" %}

{% section name:"hero" %}
<div class="hero">
  <div class="hero-overlay"></div>
  <div class="hero-content">
    <p class="hero-tag">COMPETITIVE GAMING PLATFORM</p>
    <h1 class="hero-title">Compete. <span class="accent">Conquer.</span> Rise.</h1>
    <p class="hero-subtitle">Join tournaments, climb leaderboards, and prove you're the best.</p>
    <div class="hero-actions">
      {% nav-link to:"/tournaments" %}<span class="btn btn-primary">Browse Tournaments</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="btn btn-outline">Leaderboards</span>{% end-nav-link %}
    </div>
  </div>
</div>
{% endsection %}

{% section name:"upcoming-tournaments" %}
<div class="container">
  <div class="section-header">
    <p class="section-tag">UPCOMING</p>
    <h2 class="section-title">Tournaments</h2>
  </div>

  {% capture query %}
  query tournaments($query: TournamentsQuery!, $page: PageInfo!) {
    tournaments(query: $query, page: $page) {
      nodes {
        id
        title
        description
        startAt
        endAt
        startRegistrationsAt
        endRegistrationsAt
      }
      totalCount
    }
  }
  {% endcapture %}

  {% capture variables %}
  {
    "query": {
      "orderBy": "START_AT",
      "orderDirection": "ASC",
      "status": "UPCOMING"
    },
    "page": {
      "first": 6,
      "after": null
    }
  }
  {% endcapture %}

  {% api-query query:query variables:variables %}
    {% if error %}
      <div class="alert alert-error">{{ error }}</div>
    {% endif %}

    {% if result.tournaments.totalCount == 0 %}
      <div class="empty-state">
        <div class="empty-icon">&#127942;</div>
        <p>No upcoming tournaments yet. Check back soon!</p>
      </div>
    {% endif %}

    <div class="card-grid">
      {% for tournament in result.tournaments.nodes %}
        {% capture url %}/tournaments/{{ tournament.id }}{% endcapture %}
        {% nav-link to:url %}
          <div class="tournament-card">
            <div class="card-accent-bar"></div>
            <div class="card-body">
              <div class="card-badge">TOURNAMENT</div>
              <h3 class="card-title">{{ tournament.title }}</h3>
              <div class="card-meta">
                <div class="meta-item">
                  <span class="meta-label">Starts</span>
                  <span class="meta-value">{{ tournament.startAt | date: "%b %d, %Y" }}</span>
                </div>
                <div class="meta-item">
                  <span class="meta-label">Registration</span>
                  <span class="meta-value">{{ tournament.startRegistrationsAt | date: "%b %d" }} - {{ tournament.endRegistrationsAt | date: "%b %d" }}</span>
                </div>
              </div>
            </div>
            <div class="card-footer">
              <span class="card-link-text">View Details &rarr;</span>
            </div>
          </div>
        {% end-nav-link %}
      {% endfor %}
    </div>

    {% if result.tournaments.totalCount > 6 %}
      <div class="section-cta">
        {% nav-link to:"/tournaments" %}<span class="btn btn-outline">View All Tournaments</span>{% end-nav-link %}
      </div>
    {% endif %}
  {% end-api-query %}
</div>
{% endsection %}

{% section name:"leaderboard-preview" %}
<div class="container">
  <div class="section-header">
    <p class="section-tag">TOP PLAYERS</p>
    <h2 class="section-title">Leaderboard</h2>
  </div>

  {% capture lbQuery %}
  query leaderboards($query: LeaderboardsQuery!, $page: PageInfo!) {
    leaderboards(query: $query, page: $page) {
      nodes {
        id
        title
      }
    }
  }
  {% endcapture %}

  {% capture lbVariables %}
  {
    "query": {
      "orderBy": "CREATED_AT",
      "orderDirection": "DESC"
    },
    "page": {
      "first": 1,
      "after": null
    }
  }
  {% endcapture %}

  {% api-query query:lbQuery variables:lbVariables %}
    {% if result.leaderboards.nodes.size > 0 %}
      {% assign lb = result.leaderboards.nodes[0] %}
      <div class="leaderboard-mini">
        <div class="leaderboard-header-bar">
          <h3>{{ lb.title }}</h3>
          {% nav-link to:"/leaderboards" %}<span class="view-all-link">View All &rarr;</span>{% end-nav-link %}
        </div>
        <div class="leaderboard-placeholder">
          <p class="text-muted">Visit the leaderboards page for full rankings.</p>
        </div>
      </div>
    {% else %}
      <div class="empty-state">
        <p class="text-muted">No leaderboards configured yet.</p>
      </div>
    {% endif %}
  {% end-api-query %}
</div>
{% endsection %}

{% section name:"footer" %}
<footer class="site-footer">
  <div class="container">
    <div class="footer-grid">
      <div class="footer-brand">
        <h3 class="footer-logo">WellPlayed</h3>
        <p class="footer-desc">Your competitive gaming platform. Organize, compete, and rise through the ranks.</p>
      </div>
      <div class="footer-links">
        <h4 class="footer-heading">Navigation</h4>
        <ul class="footer-nav">
          <li>{% nav-link to:"/" %}<span class="footer-link">Home</span>{% end-nav-link %}</li>
          <li>{% nav-link to:"/tournaments" %}<span class="footer-link">Tournaments</span>{% end-nav-link %}</li>
          <li>{% nav-link to:"/leaderboards" %}<span class="footer-link">Leaderboards</span>{% end-nav-link %}</li>
          <li>{% nav-link to:"/profile" %}<span class="footer-link">My Profile</span>{% end-nav-link %}</li>
        </ul>
      </div>
      <div class="footer-links">
        <h4 class="footer-heading">Connect</h4>
        <ul class="footer-nav">
          <li><a href="#" class="footer-link" target="_blank" rel="noopener">Discord</a></li>
          <li><a href="#" class="footer-link" target="_blank" rel="noopener">Twitter / X</a></li>
          <li><a href="#" class="footer-link" target="_blank" rel="noopener">Twitch</a></li>
        </ul>
      </div>
    </div>
    <div class="footer-bottom">
      <p class="copyright">&copy; 2025 WellPlayed. All rights reserved.</p>
    </div>
  </div>
</footer>
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; scroll-behavior: smooth; }
  body {
    font-family: var(--wp-font-family);
    background-color: var(--wp-color-bg);
    color: var(--wp-color-text);
    line-height: 1.6;
    -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  ul { list-style: none; }
  img { max-width: 100%; display: block; }

  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
  .accent { color: var(--wp-color-primary); }
  .text-muted { color: var(--wp-color-text-muted); }

  /* Buttons */
  .btn {
    display: inline-block; padding: 0.875rem 2rem; font-weight: 600;
    font-size: 0.9375rem; border-radius: var(--wp-border-radius);
    transition: all 0.25s ease; cursor: pointer; text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  .btn-primary {
    background: var(--wp-color-primary); color: var(--wp-color-bg);
  }
  .btn-primary:hover { filter: brightness(1.15); transform: translateY(-1px); }
  .btn-outline {
    background: transparent; color: var(--wp-color-primary);
    border: 2px solid var(--wp-color-primary);
  }
  .btn-outline:hover { background: var(--wp-color-primary); color: var(--wp-color-bg); }

  /* Hero */
  .hero {
    position: relative; min-height: 90vh; display: flex; align-items: center;
    justify-content: center; text-align: center; padding: 6rem 1.5rem;
    background: linear-gradient(135deg, var(--wp-color-bg) 0%, var(--wp-color-surface) 50%, var(--wp-color-bg) 100%);
    overflow: hidden;
  }
  .hero::before {
    content: ""; position: absolute; top: -50%; left: -50%;
    width: 200%; height: 200%;
    background: radial-gradient(circle at 30% 50%, rgba(44,177,188,0.08) 0%, transparent 50%),
                radial-gradient(circle at 70% 50%, rgba(124,58,237,0.06) 0%, transparent 50%);
    animation: heroShift 20s ease-in-out infinite alternate;
  }
  @keyframes heroShift { 0% { transform: translate(0,0); } 100% { transform: translate(3%,2%); } }
  .hero-overlay {
    position: absolute; inset: 0;
    background: linear-gradient(180deg, transparent 60%, var(--wp-color-bg) 100%);
  }
  .hero-content { position: relative; z-index: 1; max-width: 800px; }
  .hero-tag {
    color: var(--wp-color-primary); font-weight: 700; font-size: 0.8125rem;
    letter-spacing: 3px; margin-bottom: 1.25rem;
  }
  .hero-title {
    font-family: var(--wp-font-heading); font-size: clamp(2.5rem, 6vw, 4.5rem);
    font-weight: 800; line-height: 1.1; margin-bottom: 1.5rem;
  }
  .hero-subtitle {
    font-size: 1.25rem; color: var(--wp-color-text-muted);
    max-width: 550px; margin: 0 auto 2.5rem;
  }
  .hero-actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

  /* Sections */
  .wp-section { padding: 5rem 0; }
  .section-header { text-align: center; margin-bottom: 3rem; }
  .section-tag {
    color: var(--wp-color-primary); font-weight: 700; font-size: 0.8125rem;
    letter-spacing: 2px; margin-bottom: 0.5rem;
  }
  .section-title {
    font-family: var(--wp-font-heading); font-size: 2.25rem; font-weight: 800;
  }
  .section-cta { text-align: center; margin-top: 2.5rem; }

  /* Card Grid */
  .card-grid {
    display: grid; gap: 1.5rem;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  }
  .tournament-card {
    background: var(--wp-color-surface); border-radius: var(--wp-border-radius);
    overflow: hidden; transition: transform 0.25s ease, box-shadow 0.25s ease;
    display: flex; flex-direction: column;
    border: 1px solid rgba(255,255,255,0.05);
  }
  .tournament-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0,0,0,0.4);
  }
  .card-accent-bar { height: 3px; background: linear-gradient(90deg, var(--wp-color-primary), var(--wp-color-secondary)); }
  .card-body { padding: 1.5rem; flex: 1; }
  .card-badge {
    display: inline-block; padding: 0.25rem 0.75rem;
    background: rgba(44,177,188,0.1); color: var(--wp-color-primary);
    font-size: 0.6875rem; font-weight: 700; letter-spacing: 1px;
    border-radius: 4px; margin-bottom: 0.75rem;
  }
  .card-title { font-family: var(--wp-font-heading); font-size: 1.25rem; font-weight: 700; margin-bottom: 1rem; }
  .card-meta { display: flex; gap: 1.5rem; flex-wrap: wrap; }
  .meta-item { display: flex; flex-direction: column; }
  .meta-label { font-size: 0.6875rem; color: var(--wp-color-text-muted); text-transform: uppercase; letter-spacing: 1px; }
  .meta-value { font-size: 0.875rem; font-weight: 600; }
  .card-footer {
    padding: 1rem 1.5rem; border-top: 1px solid rgba(255,255,255,0.05);
  }
  .card-link-text { color: var(--wp-color-primary); font-weight: 600; font-size: 0.875rem; }

  /* Leaderboard Mini */
  .leaderboard-mini {
    background: var(--wp-color-surface); border-radius: var(--wp-border-radius);
    padding: 2rem; border: 1px solid rgba(255,255,255,0.05);
  }
  .leaderboard-header-bar {
    display: flex; justify-content: space-between; align-items: center; margin-bottom: 1rem;
  }
  .leaderboard-header-bar h3 { font-family: var(--wp-font-heading); font-size: 1.125rem; }
  .view-all-link { color: var(--wp-color-primary); font-size: 0.875rem; font-weight: 600; }
  .leaderboard-placeholder { padding: 2rem; text-align: center; }

  /* Empty State */
  .empty-state { text-align: center; padding: 4rem 1rem; }
  .empty-icon { font-size: 3rem; margin-bottom: 1rem; }

  /* Alert */
  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); margin-bottom: 1.5rem; }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

  /* Footer */
  .site-footer {
    background: var(--wp-color-surface); border-top: 1px solid rgba(255,255,255,0.05);
    padding: 4rem 0 0;
  }
  .footer-grid {
    display: grid; gap: 2rem;
    grid-template-columns: 2fr 1fr 1fr;
  }
  .footer-logo {
    font-family: var(--wp-font-heading); font-size: 1.5rem; font-weight: 800;
    color: var(--wp-color-primary); margin-bottom: 0.75rem;
  }
  .footer-desc { color: var(--wp-color-text-muted); font-size: 0.9375rem; max-width: 320px; }
  .footer-heading {
    font-family: var(--wp-font-heading); font-size: 0.875rem;
    text-transform: uppercase; letter-spacing: 1px; margin-bottom: 1rem;
  }
  .footer-nav { display: flex; flex-direction: column; gap: 0.5rem; }
  .footer-link {
    color: var(--wp-color-text-muted); font-size: 0.9375rem;
    transition: color 0.2s; display: inline-block;
  }
  .footer-link:hover { color: var(--wp-color-primary); }
  .footer-bottom {
    margin-top: 3rem; padding: 1.5rem 0;
    border-top: 1px solid rgba(255,255,255,0.05); text-align: center;
  }
  .copyright { color: var(--wp-color-text-muted); font-size: 0.8125rem; }

  @media (max-width: 768px) {
    .card-grid { grid-template-columns: 1fr; }
    .footer-grid { grid-template-columns: 1fr; }
    .hero { min-height: 70vh; padding: 4rem 1.5rem; }
  }
</style>`,
  },
  {
    name: "Tournament List",
    template: `{% theme %}
{% title "Tournaments" %}
{% favicon "favicon.svg" %}

{% section name:"nav" %}
<nav class="top-nav">
  <div class="container nav-inner">
    <div class="nav-brand">
      {% nav-link to:"/" %}<span class="brand-text">WellPlayed</span>{% end-nav-link %}
    </div>
    <div class="nav-links">
      {% nav-link to:"/" %}<span class="nav-link-item">Home</span>{% end-nav-link %}
      {% nav-link to:"/tournaments" %}<span class="nav-link-item active">Tournaments</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="nav-link-item">Leaderboards</span>{% end-nav-link %}
      {% nav-link to:"/profile" %}<span class="nav-link-item">Profile</span>{% end-nav-link %}
    </div>
  </div>
</nav>
{% endsection %}

{% section name:"tournament-list" %}
<div class="container page-content">
  <div class="page-header">
    <h1 class="page-title">Tournaments</h1>
    <p class="page-subtitle">Find and join competitive tournaments</p>
  </div>

  <div class="filter-bar">
    <div class="filter-group">
      <span class="filter-label">Status:</span>
      <span class="filter-chip filter-active">All</span>
      <span class="filter-chip">Upcoming</span>
      <span class="filter-chip">Live</span>
      <span class="filter-chip">Completed</span>
    </div>
  </div>

  {% capture query %}
  query tournaments($query: TournamentsQuery!, $page: PageInfo!) {
    tournaments(query: $query, page: $page) {
      nodes {
        id
        title
        description
        startAt
        endAt
        startRegistrationsAt
        endRegistrationsAt
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        startCursor
        endCursor
      }
      totalCount
    }
  }
  {% endcapture %}

  {% capture variables %}
  {
    "query": {
      "orderBy": "START_AT",
      "orderDirection": "ASC",
      "status": "ALL"
    },
    "page": {
      "first": 12,
      "after": null
    }
  }
  {% endcapture %}

  {% api-query query:query variables:variables %}
    {% if error %}
      <div class="alert alert-error">{{ error }}</div>
    {% endif %}

    {% if result.tournaments.totalCount == 0 %}
      <div class="empty-state">
        <div class="empty-icon">&#128269;</div>
        <h3>No Tournaments Found</h3>
        <p class="text-muted">There are no tournaments matching your criteria.</p>
      </div>
    {% endif %}

    <div class="results-bar">
      <span class="results-count">{{ result.tournaments.totalCount }} tournament{% if result.tournaments.totalCount != 1 %}s{% endif %} found</span>
    </div>

    <div class="card-grid">
      {% for tournament in result.tournaments.nodes %}
        {% capture url %}/tournaments/{{ tournament.id }}{% endcapture %}
        {% nav-link to:url %}
          <div class="tournament-card">
            <div class="card-gradient-top"></div>
            <div class="card-body">
              <div class="card-badge">TOURNAMENT</div>
              <h3 class="card-title">{{ tournament.title }}</h3>
              <div class="card-dates">
                <div class="date-row">
                  <span class="date-icon">&#128197;</span>
                  <span>{{ tournament.startAt | date: "%b %d, %Y %H:%M" }}</span>
                </div>
                {% if tournament.endAt %}
                <div class="date-row">
                  <span class="date-icon">&#127937;</span>
                  <span>{{ tournament.endAt | date: "%b %d, %Y %H:%M" }}</span>
                </div>
                {% endif %}
              </div>
              {% if tournament.startRegistrationsAt %}
              <div class="registration-info">
                <span class="reg-label">Registration:</span>
                <span>{{ tournament.startRegistrationsAt | date: "%b %d" }} &ndash; {{ tournament.endRegistrationsAt | date: "%b %d" }}</span>
              </div>
              {% endif %}
            </div>
            <div class="card-footer">
              <span class="card-action">View Details &rarr;</span>
            </div>
          </div>
        {% end-nav-link %}
      {% endfor %}
    </div>

    <div class="pagination">
      {% if result.tournaments.pageInfo.hasPreviousPage %}
        <span class="btn btn-outline btn-sm">&larr; Previous</span>
      {% endif %}
      {% if result.tournaments.pageInfo.hasNextPage %}
        <span class="btn btn-outline btn-sm">Next &rarr;</span>
      {% endif %}
    </div>
  {% end-api-query %}
</div>
{% endsection %}

{% section name:"footer" %}
<footer class="site-footer">
  <div class="container footer-inner">
    <p class="copyright">&copy; 2025 WellPlayed. All rights reserved.</p>
    <div class="footer-links">
      {% nav-link to:"/" %}<span class="footer-link">Home</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="footer-link">Leaderboards</span>{% end-nav-link %}
    </div>
  </div>
</footer>
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; scroll-behavior: smooth; }
  body {
    font-family: var(--wp-font-family); background-color: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  ul { list-style: none; }
  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
  .text-muted { color: var(--wp-color-text-muted); }

  /* Nav */
  .top-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(15,15,15,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 0.875rem 0;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; }
  .brand-text {
    font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 800;
    color: var(--wp-color-primary);
  }
  .nav-links { display: flex; gap: 1.5rem; }
  .nav-link-item {
    font-size: 0.9375rem; font-weight: 500; color: var(--wp-color-text-muted);
    transition: color 0.2s; padding: 0.25rem 0;
  }
  .nav-link-item:hover, .nav-link-item.active { color: var(--wp-color-primary); }

  /* Page */
  .page-content { padding: 3rem 0 5rem; }
  .page-header { margin-bottom: 2rem; }
  .page-title { font-family: var(--wp-font-heading); font-size: 2.5rem; font-weight: 800; }
  .page-subtitle { color: var(--wp-color-text-muted); font-size: 1.125rem; margin-top: 0.5rem; }

  /* Filter Bar */
  .filter-bar {
    display: flex; flex-wrap: wrap; gap: 1rem; align-items: center;
    margin-bottom: 1.5rem; padding: 1rem 1.25rem;
    background: var(--wp-color-surface); border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05);
  }
  .filter-group { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
  .filter-label { font-size: 0.8125rem; color: var(--wp-color-text-muted); font-weight: 600; }
  .filter-chip {
    padding: 0.375rem 0.875rem; font-size: 0.8125rem; font-weight: 500;
    border-radius: 100px; border: 1px solid rgba(255,255,255,0.1);
    color: var(--wp-color-text-muted); cursor: pointer; transition: all 0.2s;
  }
  .filter-chip:hover { border-color: var(--wp-color-primary); color: var(--wp-color-primary); }
  .filter-active {
    background: var(--wp-color-primary); color: var(--wp-color-bg);
    border-color: var(--wp-color-primary); font-weight: 600;
  }

  .results-bar { margin-bottom: 1rem; }
  .results-count { font-size: 0.875rem; color: var(--wp-color-text-muted); }

  /* Buttons */
  .btn {
    display: inline-block; padding: 0.75rem 1.5rem; font-weight: 600;
    font-size: 0.875rem; border-radius: var(--wp-border-radius);
    transition: all 0.25s ease; cursor: pointer;
  }
  .btn-sm { padding: 0.5rem 1.25rem; font-size: 0.8125rem; }
  .btn-primary { background: var(--wp-color-primary); color: var(--wp-color-bg); }
  .btn-outline {
    background: transparent; color: var(--wp-color-primary);
    border: 1px solid var(--wp-color-primary);
  }
  .btn-outline:hover { background: var(--wp-color-primary); color: var(--wp-color-bg); }

  /* Card Grid */
  .card-grid {
    display: grid; gap: 1.25rem;
    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  }
  .tournament-card {
    background: var(--wp-color-surface); border-radius: var(--wp-border-radius);
    overflow: hidden; transition: transform 0.2s, box-shadow 0.2s;
    border: 1px solid rgba(255,255,255,0.05); display: flex; flex-direction: column;
  }
  .tournament-card:hover { transform: translateY(-3px); box-shadow: 0 8px 30px rgba(0,0,0,0.35); }
  .card-gradient-top { height: 3px; background: linear-gradient(90deg, var(--wp-color-primary), var(--wp-color-secondary)); }
  .card-body { padding: 1.5rem; flex: 1; }
  .card-badge {
    display: inline-block; padding: 0.2rem 0.625rem; font-size: 0.6875rem;
    font-weight: 700; letter-spacing: 1px; border-radius: 4px;
    background: rgba(44,177,188,0.1); color: var(--wp-color-primary); margin-bottom: 0.75rem;
  }
  .card-title { font-family: var(--wp-font-heading); font-size: 1.125rem; font-weight: 700; margin-bottom: 0.875rem; }
  .card-dates { display: flex; flex-direction: column; gap: 0.375rem; margin-bottom: 0.75rem; }
  .date-row { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: var(--wp-color-text-muted); }
  .date-icon { font-size: 0.875rem; }
  .registration-info {
    font-size: 0.8125rem; color: var(--wp-color-text-muted);
    padding-top: 0.75rem; border-top: 1px solid rgba(255,255,255,0.05);
  }
  .reg-label { font-weight: 600; margin-right: 0.25rem; }
  .card-footer { padding: 0.875rem 1.5rem; border-top: 1px solid rgba(255,255,255,0.05); }
  .card-action { color: var(--wp-color-primary); font-weight: 600; font-size: 0.875rem; }

  /* Pagination */
  .pagination { display: flex; justify-content: center; gap: 1rem; margin-top: 2.5rem; }

  /* Empty */
  .empty-state { text-align: center; padding: 5rem 1rem; }
  .empty-icon { font-size: 3rem; margin-bottom: 1rem; }
  .empty-state h3 { font-family: var(--wp-font-heading); margin-bottom: 0.5rem; }
  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); margin-bottom: 1.5rem; }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

  /* Footer */
  .site-footer {
    background: var(--wp-color-surface); padding: 1.5rem 0;
    border-top: 1px solid rgba(255,255,255,0.05);
  }
  .footer-inner { display: flex; justify-content: space-between; align-items: center; }
  .copyright { font-size: 0.8125rem; color: var(--wp-color-text-muted); }
  .footer-links { display: flex; gap: 1.25rem; }
  .footer-link { font-size: 0.875rem; color: var(--wp-color-text-muted); transition: color 0.2s; }
  .footer-link:hover { color: var(--wp-color-primary); }

  @media (max-width: 768px) {
    .card-grid { grid-template-columns: 1fr; }
    .nav-links { gap: 0.75rem; }
    .nav-link-item { font-size: 0.8125rem; }
    .filter-bar { flex-direction: column; align-items: flex-start; }
    .footer-inner { flex-direction: column; gap: 0.75rem; text-align: center; }
  }
</style>`,
  },
  {
    name: "Tournament Detail",
    template: `{% theme %}
{% title "Tournament" %}
{% favicon "favicon.svg" %}

{% section name:"nav" %}
<nav class="top-nav">
  <div class="container nav-inner">
    {% nav-link to:"/" %}<span class="brand-text">WellPlayed</span>{% end-nav-link %}
    <div class="nav-links">
      {% nav-link to:"/" %}<span class="nav-link-item">Home</span>{% end-nav-link %}
      {% nav-link to:"/tournaments" %}<span class="nav-link-item">Tournaments</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="nav-link-item">Leaderboards</span>{% end-nav-link %}
    </div>
  </div>
</nav>
{% endsection %}

{% section name:"tournament-detail" %}
{% capture query %}
query tournament($id: ID!) {
  tournament(id: $id) {
    id
    title
    description
    startAt
    endAt
    startRegistrationsAt
    endRegistrationsAt
    createdBy {
      profiles {
        username
      }
    }
    steps {
      id
      title
      type
    }
  }
}
{% endcapture %}

{% capture variables %}
{
  "id": "{{ params.tournamentId }}"
}
{% endcapture %}

{% api-query query:query variables:variables %}
  {% if error %}
    <div class="container page-content">
      <div class="alert alert-error">{{ error }}</div>
    </div>
  {% endif %}

  {% if result.tournament %}
    <!-- Banner -->
    <div class="tournament-banner">
      <div class="banner-bg"></div>
      <div class="container banner-content">
        <div class="breadcrumb">
          {% nav-link to:"/tournaments" %}<span class="breadcrumb-link">Tournaments</span>{% end-nav-link %}
          <span class="breadcrumb-sep">/</span>
          <span class="breadcrumb-current">{{ result.tournament.title }}</span>
        </div>
        <h1 class="tournament-title">{{ result.tournament.title }}</h1>
        {% if result.tournament.createdBy.profiles[0].username %}
          <p class="tournament-organizer">Organized by <span class="accent">{{ result.tournament.createdBy.profiles[0].username }}</span></p>
        {% endif %}
      </div>
    </div>

    <div class="container page-content">
      <div class="detail-layout">
        <!-- Main Content -->
        <div class="detail-main">
          <!-- Info Grid -->
          <div class="info-grid">
            <div class="info-card">
              <span class="info-label">Start Date</span>
              <span class="info-value">{{ result.tournament.startAt | date: "%B %d, %Y" }}</span>
              <span class="info-sub">{{ result.tournament.startAt | date: "%H:%M" }}</span>
            </div>
            <div class="info-card">
              <span class="info-label">End Date</span>
              <span class="info-value">{{ result.tournament.endAt | date: "%B %d, %Y" }}</span>
              <span class="info-sub">{{ result.tournament.endAt | date: "%H:%M" }}</span>
            </div>
            <div class="info-card">
              <span class="info-label">Registration Opens</span>
              <span class="info-value">{{ result.tournament.startRegistrationsAt | date: "%B %d, %Y" }}</span>
            </div>
            <div class="info-card">
              <span class="info-label">Registration Closes</span>
              <span class="info-value">{{ result.tournament.endRegistrationsAt | date: "%B %d, %Y" }}</span>
            </div>
          </div>

          <!-- Description -->
          <div class="content-block">
            <h2 class="block-title">About This Tournament</h2>
            <div class="rich-content">
              {% rich-text content:result.tournament.description %}
            </div>
          </div>

          <!-- Bracket / Steps -->
          {% if result.tournament.steps.size > 0 %}
          <div class="content-block">
            <h2 class="block-title">Bracket & Stages</h2>
            <div class="steps-list">
              {% for step in result.tournament.steps %}
                <div class="step-card">
                  <div class="step-index">{{ forloop.index }}</div>
                  <div class="step-info">
                    <h4 class="step-title">{{ step.title }}</h4>
                    <span class="step-type">{{ step.type }}</span>
                  </div>
                </div>

                {% tournament-step tournament-id:result.tournament.id step-id:step.id %}
                  {% if result.size > 0 %}
                    <div class="bracket-viewer">
                      {% for group in result %}
                        <div class="bracket-group">
                          <h5 class="group-name">{{ group.name }}</h5>
                        </div>
                      {% endfor %}
                    </div>
                  {% endif %}
                {% end-tournament-step %}
              {% endfor %}
            </div>
          </div>
          {% endif %}

          <!-- Teams -->
          <div class="content-block">
            <h2 class="block-title">Registered Teams</h2>
            {% tournament-teams tournament-id:result.tournament.id %}
              {% if result.size > 0 %}
                <div class="teams-grid">
                  {% for team in result %}
                    <div class="team-card">
                      <div class="team-avatar">{{ team.name | slice: 0, 2 | upcase }}</div>
                      <div class="team-info">
                        <span class="team-name">{{ team.name }}</span>
                        <span class="team-status">{{ team.status }}</span>
                      </div>
                    </div>
                  {% endfor %}
                </div>
              {% else %}
                <div class="empty-state-sm">
                  <p class="text-muted">No teams registered yet. Be the first!</p>
                </div>
              {% endif %}
            {% end-tournament-teams %}
          </div>
        </div>

        <!-- Sidebar -->
        <div class="detail-sidebar">
          <div class="sidebar-card">
            <h3 class="sidebar-title">Register</h3>
            <p class="sidebar-text">Sign in to register your team for this tournament.</p>
            {% if auth.isLoggedIn %}
              {% nav-link to:"/tournaments/{{ result.tournament.id }}/registered" %}
                <span class="btn btn-primary btn-block">Register Now</span>
              {% end-nav-link %}
            {% else %}
              {% nav-link to:"/login" %}
                <span class="btn btn-primary btn-block">Sign In to Register</span>
              {% end-nav-link %}
            {% endif %}
          </div>

          <div class="sidebar-card">
            <h3 class="sidebar-title">Quick Info</h3>
            <div class="sidebar-stat">
              <span class="stat-label">Format</span>
              <span class="stat-value">{{ result.tournament.steps.size }} Stage{% if result.tournament.steps.size != 1 %}s{% endif %}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  {% endif %}
{% end-api-query %}
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; }
  body {
    font-family: var(--wp-font-family); background: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  ul { list-style: none; }
  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
  .accent { color: var(--wp-color-primary); }
  .text-muted { color: var(--wp-color-text-muted); }

  .top-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(15,15,15,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 0.875rem 0;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; }
  .brand-text { font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 800; color: var(--wp-color-primary); }
  .nav-links { display: flex; gap: 1.5rem; }
  .nav-link-item { font-size: 0.9375rem; font-weight: 500; color: var(--wp-color-text-muted); transition: color 0.2s; }
  .nav-link-item:hover { color: var(--wp-color-primary); }

  /* Banner */
  .tournament-banner {
    position: relative; padding: 4rem 0 3rem; overflow: hidden;
  }
  .banner-bg {
    position: absolute; inset: 0;
    background: linear-gradient(135deg, var(--wp-color-surface) 0%, var(--wp-color-bg) 100%);
  }
  .banner-bg::after {
    content: ""; position: absolute; inset: 0;
    background: linear-gradient(90deg, rgba(44,177,188,0.05) 0%, rgba(124,58,237,0.05) 100%);
  }
  .banner-content { position: relative; z-index: 1; }
  .breadcrumb { display: flex; align-items: center; gap: 0.5rem; margin-bottom: 1rem; font-size: 0.875rem; }
  .breadcrumb-link { color: var(--wp-color-primary); }
  .breadcrumb-sep { color: var(--wp-color-text-muted); }
  .breadcrumb-current { color: var(--wp-color-text-muted); }
  .tournament-title { font-family: var(--wp-font-heading); font-size: clamp(2rem, 4vw, 3rem); font-weight: 800; }
  .tournament-organizer { color: var(--wp-color-text-muted); margin-top: 0.5rem; font-size: 1rem; }

  .page-content { padding: 2.5rem 0 5rem; }

  /* Layout */
  .detail-layout { display: grid; grid-template-columns: 1fr 340px; gap: 2rem; }

  /* Info Grid */
  .info-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; margin-bottom: 2rem; }
  .info-card {
    background: var(--wp-color-surface); padding: 1.25rem; border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05); display: flex; flex-direction: column;
  }
  .info-label { font-size: 0.6875rem; text-transform: uppercase; letter-spacing: 1px; color: var(--wp-color-text-muted); font-weight: 600; margin-bottom: 0.375rem; }
  .info-value { font-family: var(--wp-font-heading); font-weight: 700; font-size: 1rem; }
  .info-sub { font-size: 0.8125rem; color: var(--wp-color-text-muted); }

  /* Content Block */
  .content-block { margin-bottom: 2.5rem; }
  .block-title { font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 700; margin-bottom: 1rem; padding-bottom: 0.75rem; border-bottom: 2px solid var(--wp-color-primary); }
  .rich-content { font-size: 0.9375rem; line-height: 1.8; color: var(--wp-color-text-muted); }

  /* Steps */
  .steps-list { display: flex; flex-direction: column; gap: 1rem; }
  .step-card {
    display: flex; align-items: center; gap: 1rem;
    background: var(--wp-color-surface); padding: 1rem 1.25rem;
    border-radius: var(--wp-border-radius); border: 1px solid rgba(255,255,255,0.05);
  }
  .step-index {
    width: 2.5rem; height: 2.5rem; display: flex; align-items: center; justify-content: center;
    background: var(--wp-color-primary); color: var(--wp-color-bg); border-radius: 50%;
    font-weight: 800; font-family: var(--wp-font-heading); font-size: 1rem; flex-shrink: 0;
  }
  .step-title { font-family: var(--wp-font-heading); font-weight: 700; font-size: 1rem; }
  .step-type { font-size: 0.75rem; color: var(--wp-color-text-muted); text-transform: uppercase; letter-spacing: 1px; }

  .bracket-viewer { padding: 1rem 0 0 3.5rem; }
  .bracket-group { padding: 0.5rem 0; }
  .group-name { font-size: 0.875rem; color: var(--wp-color-text-muted); }

  /* Teams */
  .teams-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 0.75rem; }
  .team-card {
    display: flex; align-items: center; gap: 0.75rem;
    background: var(--wp-color-surface); padding: 0.875rem 1rem;
    border-radius: var(--wp-border-radius); border: 1px solid rgba(255,255,255,0.05);
  }
  .team-avatar {
    width: 2.25rem; height: 2.25rem; border-radius: 50%;
    background: linear-gradient(135deg, var(--wp-color-primary), var(--wp-color-secondary));
    display: flex; align-items: center; justify-content: center;
    font-weight: 700; font-size: 0.6875rem; color: #fff; flex-shrink: 0;
  }
  .team-name { font-weight: 600; font-size: 0.9375rem; display: block; }
  .team-status { font-size: 0.75rem; color: var(--wp-color-text-muted); text-transform: uppercase; }
  .empty-state-sm { padding: 2rem; text-align: center; }

  /* Sidebar */
  .detail-sidebar { display: flex; flex-direction: column; gap: 1.25rem; }
  .sidebar-card {
    background: var(--wp-color-surface); padding: 1.5rem; border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05);
  }
  .sidebar-title { font-family: var(--wp-font-heading); font-size: 1.125rem; font-weight: 700; margin-bottom: 0.75rem; }
  .sidebar-text { color: var(--wp-color-text-muted); font-size: 0.9375rem; margin-bottom: 1.25rem; }
  .btn { display: inline-block; padding: 0.75rem 1.5rem; font-weight: 600; font-size: 0.875rem; border-radius: var(--wp-border-radius); transition: all 0.2s; cursor: pointer; text-align: center; }
  .btn-primary { background: var(--wp-color-primary); color: var(--wp-color-bg); }
  .btn-primary:hover { filter: brightness(1.15); }
  .btn-block { display: block; width: 100%; }
  .sidebar-stat { display: flex; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid rgba(255,255,255,0.05); }
  .stat-label { color: var(--wp-color-text-muted); font-size: 0.875rem; }
  .stat-value { font-weight: 600; font-size: 0.875rem; }

  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); margin-bottom: 1.5rem; }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

  @media (max-width: 968px) {
    .detail-layout { grid-template-columns: 1fr; }
    .info-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 640px) {
    .info-grid { grid-template-columns: 1fr; }
    .teams-grid { grid-template-columns: 1fr; }
    .nav-links { gap: 0.75rem; }
    .nav-link-item { font-size: 0.8125rem; }
  }
</style>`,
  },
  {
    name: "Registration Confirmed",
    template: `{% theme %}
{% title "Registration Confirmed" %}
{% favicon "favicon.svg" %}

{% section name:"nav" %}
<nav class="top-nav">
  <div class="container nav-inner">
    {% nav-link to:"/" %}<span class="brand-text">WellPlayed</span>{% end-nav-link %}
    <div class="nav-links">
      {% nav-link to:"/" %}<span class="nav-link-item">Home</span>{% end-nav-link %}
      {% nav-link to:"/tournaments" %}<span class="nav-link-item">Tournaments</span>{% end-nav-link %}
    </div>
  </div>
</nav>
{% endsection %}

{% section name:"confirmation" %}
<div class="container page-content">
  {% capture query %}
  query tournament($id: ID!) {
    tournament(id: $id) {
      id
      title
      startAt
      endAt
    }
  }
  {% endcapture %}

  {% capture variables %}
  {
    "id": "{{ params.tournamentId }}"
  }
  {% endcapture %}

  {% api-query query:query variables:variables %}
    {% if error %}
      <div class="alert alert-error">{{ error }}</div>
    {% endif %}

    {% if result.tournament %}
      <div class="confirmation-wrapper">
        <div class="success-card">
          <div class="success-icon-ring">
            <div class="success-icon">&#10003;</div>
          </div>
          <h1 class="success-title">You're In!</h1>
          <p class="success-subtitle">Your registration for <strong>{{ result.tournament.title }}</strong> has been confirmed.</p>

          <div class="summary-card">
            <h3 class="summary-heading">Tournament Summary</h3>
            <div class="summary-grid">
              <div class="summary-item">
                <span class="summary-label">Tournament</span>
                <span class="summary-value">{{ result.tournament.title }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Start Date</span>
                <span class="summary-value">{{ result.tournament.startAt | date: "%B %d, %Y at %H:%M" }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">End Date</span>
                <span class="summary-value">{{ result.tournament.endAt | date: "%B %d, %Y at %H:%M" }}</span>
              </div>
              <div class="summary-item">
                <span class="summary-label">Status</span>
                <span class="status-badge">REGISTERED</span>
              </div>
            </div>
          </div>

          <div class="next-steps">
            <h3 class="next-steps-title">What's Next?</h3>
            <ul class="steps-checklist">
              <li class="checklist-item">
                <span class="check-marker">1</span>
                <span>Make sure your team roster is complete before the tournament starts.</span>
              </li>
              <li class="checklist-item">
                <span class="check-marker">2</span>
                <span>Check in on the tournament page for schedule updates and bracket info.</span>
              </li>
              <li class="checklist-item">
                <span class="check-marker">3</span>
                <span>Show up on time and compete. Good luck!</span>
              </li>
            </ul>
          </div>

          <div class="confirmation-actions">
            {% capture tournamentUrl %}/tournaments/{{ result.tournament.id }}{% endcapture %}
            {% nav-link to:tournamentUrl %}<span class="btn btn-primary">Back to Tournament</span>{% end-nav-link %}
            {% nav-link to:"/tournaments" %}<span class="btn btn-outline">Browse More Tournaments</span>{% end-nav-link %}
          </div>
        </div>
      </div>
    {% endif %}
  {% end-api-query %}
</div>
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; }
  body {
    font-family: var(--wp-font-family); background: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }

  .top-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(15,15,15,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 0.875rem 0;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; }
  .brand-text { font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 800; color: var(--wp-color-primary); }
  .nav-links { display: flex; gap: 1.5rem; }
  .nav-link-item { font-size: 0.9375rem; font-weight: 500; color: var(--wp-color-text-muted); transition: color 0.2s; }
  .nav-link-item:hover { color: var(--wp-color-primary); }

  .page-content { padding: 4rem 0 6rem; }

  .confirmation-wrapper { max-width: 680px; margin: 0 auto; }

  .success-card { text-align: center; }
  .success-icon-ring {
    width: 5rem; height: 5rem; margin: 0 auto 1.5rem; border-radius: 50%;
    background: linear-gradient(135deg, var(--wp-color-primary), var(--wp-color-secondary));
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 0 40px rgba(44,177,188,0.25);
  }
  .success-icon { font-size: 2rem; color: #fff; font-weight: 800; }
  .success-title { font-family: var(--wp-font-heading); font-size: 2.5rem; font-weight: 800; margin-bottom: 0.75rem; }
  .success-subtitle { font-size: 1.125rem; color: var(--wp-color-text-muted); margin-bottom: 2.5rem; max-width: 480px; margin-left: auto; margin-right: auto; }
  .success-subtitle strong { color: var(--wp-color-text); }

  .summary-card {
    background: var(--wp-color-surface); border-radius: var(--wp-border-radius);
    padding: 1.75rem; margin-bottom: 2rem; text-align: left;
    border: 1px solid rgba(255,255,255,0.05);
  }
  .summary-heading { font-family: var(--wp-font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 1.25rem; text-transform: uppercase; letter-spacing: 0.5px; }
  .summary-grid { display: flex; flex-direction: column; gap: 0.875rem; }
  .summary-item { display: flex; justify-content: space-between; align-items: center; padding-bottom: 0.875rem; border-bottom: 1px solid rgba(255,255,255,0.05); }
  .summary-item:last-child { border-bottom: none; padding-bottom: 0; }
  .summary-label { color: var(--wp-color-text-muted); font-size: 0.875rem; }
  .summary-value { font-weight: 600; font-size: 0.9375rem; }
  .status-badge {
    display: inline-block; padding: 0.25rem 0.75rem; border-radius: 100px;
    background: rgba(34,197,94,0.12); color: #4ade80; font-size: 0.75rem;
    font-weight: 700; letter-spacing: 0.5px;
  }

  .next-steps { text-align: left; margin-bottom: 2.5rem; }
  .next-steps-title { font-family: var(--wp-font-heading); font-size: 1.125rem; font-weight: 700; margin-bottom: 1rem; }
  .steps-checklist { display: flex; flex-direction: column; gap: 0.875rem; }
  .checklist-item {
    display: flex; align-items: flex-start; gap: 0.875rem;
    background: var(--wp-color-surface); padding: 1rem 1.25rem;
    border-radius: var(--wp-border-radius); border: 1px solid rgba(255,255,255,0.05);
    font-size: 0.9375rem; color: var(--wp-color-text-muted);
  }
  .check-marker {
    flex-shrink: 0; width: 1.5rem; height: 1.5rem; border-radius: 50%;
    background: var(--wp-color-primary); color: var(--wp-color-bg);
    display: flex; align-items: center; justify-content: center;
    font-size: 0.75rem; font-weight: 700;
  }

  .confirmation-actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }
  .btn {
    display: inline-block; padding: 0.875rem 2rem; font-weight: 600; font-size: 0.9375rem;
    border-radius: var(--wp-border-radius); transition: all 0.2s; cursor: pointer;
  }
  .btn-primary { background: var(--wp-color-primary); color: var(--wp-color-bg); }
  .btn-primary:hover { filter: brightness(1.15); }
  .btn-outline { background: transparent; color: var(--wp-color-primary); border: 2px solid var(--wp-color-primary); }
  .btn-outline:hover { background: var(--wp-color-primary); color: var(--wp-color-bg); }

  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); margin-bottom: 1.5rem; }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

  @media (max-width: 640px) {
    .success-title { font-size: 1.75rem; }
    .confirmation-actions { flex-direction: column; }
    .btn { width: 100%; text-align: center; }
  }
</style>`,
  },
  {
    name: "Leaderboards",
    template: `{% theme %}
{% title "Leaderboards" %}
{% favicon "favicon.svg" %}

{% section name:"nav" %}
<nav class="top-nav">
  <div class="container nav-inner">
    {% nav-link to:"/" %}<span class="brand-text">WellPlayed</span>{% end-nav-link %}
    <div class="nav-links">
      {% nav-link to:"/" %}<span class="nav-link-item">Home</span>{% end-nav-link %}
      {% nav-link to:"/tournaments" %}<span class="nav-link-item">Tournaments</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="nav-link-item active">Leaderboards</span>{% end-nav-link %}
      {% nav-link to:"/profile" %}<span class="nav-link-item">Profile</span>{% end-nav-link %}
    </div>
  </div>
</nav>
{% endsection %}

{% section name:"leaderboards" %}
<div class="container page-content">
  <div class="page-header">
    <h1 class="page-title">Leaderboards</h1>
    <p class="page-subtitle">Track rankings and see who's on top</p>
  </div>

  {% capture query %}
  query leaderboards($query: LeaderboardsQuery!, $page: PageInfo!) {
    leaderboards(query: $query, page: $page) {
      nodes {
        id
        title
        description
      }
      totalCount
    }
  }
  {% endcapture %}

  {% capture variables %}
  {
    "query": {
      "orderBy": "CREATED_AT",
      "orderDirection": "DESC"
    },
    "page": {
      "first": 20,
      "after": null
    }
  }
  {% endcapture %}

  {% api-query query:query variables:variables %}
    {% if error %}
      <div class="alert alert-error">{{ error }}</div>
    {% endif %}

    {% if result.leaderboards.totalCount == 0 %}
      <div class="empty-state">
        <div class="empty-icon">&#127942;</div>
        <h3>No Leaderboards Yet</h3>
        <p class="text-muted">Leaderboards will appear here once they are configured.</p>
      </div>
    {% endif %}

    <div class="leaderboard-list">
      {% for lb in result.leaderboards.nodes %}
        <div class="leaderboard-section">
          <div class="lb-header">
            <div class="lb-header-left">
              <div class="lb-icon-wrap">
                <span class="lb-icon">&#127942;</span>
              </div>
              <div>
                <h2 class="lb-title">{{ lb.title }}</h2>
                {% if lb.description %}
                  <p class="lb-desc">{{ lb.description }}</p>
                {% endif %}
              </div>
            </div>
          </div>

          {% capture scoresQuery %}
          query leaderboardScores($leaderboardId: ID!, $page: PageInfo!) {
            leaderboardScores(leaderboardId: $leaderboardId, page: $page) {
              nodes {
                score
                rank
                player {
                  id
                  profiles {
                    username
                  }
                }
              }
              totalCount
            }
          }
          {% endcapture %}

          {% capture scoresVars %}
          {
            "leaderboardId": "{{ lb.id }}",
            "page": {
              "first": 10,
              "after": null
            }
          }
          {% endcapture %}

          {% api-query query:scoresQuery variables:scoresVars %}
            {% if result.leaderboardScores.totalCount > 0 %}
              <div class="scores-table-wrap">
                <table class="scores-table">
                  <thead>
                    <tr>
                      <th class="col-rank">Rank</th>
                      <th class="col-player">Player</th>
                      <th class="col-score">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {% for entry in result.leaderboardScores.nodes %}
                      <tr class="{% if entry.rank <= 3 %}row-top{% endif %}">
                        <td class="col-rank">
                          {% if entry.rank == 1 %}
                            <span class="rank-badge rank-gold">1</span>
                          {% elsif entry.rank == 2 %}
                            <span class="rank-badge rank-silver">2</span>
                          {% elsif entry.rank == 3 %}
                            <span class="rank-badge rank-bronze">3</span>
                          {% else %}
                            <span class="rank-num">{{ entry.rank }}</span>
                          {% endif %}
                        </td>
                        <td class="col-player">
                          {% if entry.player.profiles[0].username %}
                            {% capture playerUrl %}/players/{{ entry.player.id }}{% endcapture %}
                            {% nav-link to:playerUrl %}
                              <span class="player-link">{{ entry.player.profiles[0].username }}</span>
                            {% end-nav-link %}
                          {% else %}
                            <span class="text-muted">Unknown Player</span>
                          {% endif %}
                        </td>
                        <td class="col-score">
                          <span class="score-value">{{ entry.score }}</span>
                        </td>
                      </tr>
                    {% endfor %}
                  </tbody>
                </table>
              </div>
            {% else %}
              <div class="empty-state-sm">
                <p class="text-muted">No scores recorded yet for this leaderboard.</p>
              </div>
            {% endif %}
          {% end-api-query %}
        </div>
      {% endfor %}
    </div>
  {% end-api-query %}
</div>
{% endsection %}

{% section name:"footer" %}
<footer class="site-footer">
  <div class="container footer-inner">
    <p class="copyright">&copy; 2025 WellPlayed. All rights reserved.</p>
  </div>
</footer>
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; }
  body {
    font-family: var(--wp-font-family); background: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  ul { list-style: none; }
  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
  .text-muted { color: var(--wp-color-text-muted); }

  .top-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(15,15,15,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 0.875rem 0;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; }
  .brand-text { font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 800; color: var(--wp-color-primary); }
  .nav-links { display: flex; gap: 1.5rem; }
  .nav-link-item { font-size: 0.9375rem; font-weight: 500; color: var(--wp-color-text-muted); transition: color 0.2s; }
  .nav-link-item:hover, .nav-link-item.active { color: var(--wp-color-primary); }

  .page-content { padding: 3rem 0 5rem; }
  .page-header { margin-bottom: 2.5rem; }
  .page-title { font-family: var(--wp-font-heading); font-size: 2.5rem; font-weight: 800; }
  .page-subtitle { color: var(--wp-color-text-muted); font-size: 1.125rem; margin-top: 0.5rem; }

  .leaderboard-list { display: flex; flex-direction: column; gap: 2rem; }

  .leaderboard-section {
    background: var(--wp-color-surface); border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05); overflow: hidden;
  }
  .lb-header {
    display: flex; justify-content: space-between; align-items: center;
    padding: 1.5rem; border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .lb-header-left { display: flex; align-items: center; gap: 1rem; }
  .lb-icon-wrap {
    width: 2.75rem; height: 2.75rem; border-radius: var(--wp-border-radius);
    background: linear-gradient(135deg, var(--wp-color-primary), var(--wp-color-secondary));
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  }
  .lb-icon { font-size: 1.25rem; }
  .lb-title { font-family: var(--wp-font-heading); font-size: 1.25rem; font-weight: 700; }
  .lb-desc { color: var(--wp-color-text-muted); font-size: 0.875rem; margin-top: 0.125rem; }

  /* Table */
  .scores-table-wrap { overflow-x: auto; }
  .scores-table { width: 100%; border-collapse: collapse; }
  .scores-table th {
    text-align: left; padding: 0.75rem 1.5rem; font-size: 0.6875rem;
    text-transform: uppercase; letter-spacing: 1px; color: var(--wp-color-text-muted);
    font-weight: 600; border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .scores-table td { padding: 0.875rem 1.5rem; border-bottom: 1px solid rgba(255,255,255,0.03); }
  .scores-table tr:last-child td { border-bottom: none; }
  .scores-table tr:hover { background: rgba(255,255,255,0.02); }
  .row-top { background: rgba(44,177,188,0.03); }

  .col-rank { width: 80px; }
  .col-score { text-align: right; }
  .col-score th { text-align: right; }

  .rank-badge {
    display: inline-flex; align-items: center; justify-content: center;
    width: 1.75rem; height: 1.75rem; border-radius: 50%;
    font-size: 0.75rem; font-weight: 800;
  }
  .rank-gold { background: linear-gradient(135deg, #fbbf24, #f59e0b); color: #1a1a2e; }
  .rank-silver { background: linear-gradient(135deg, #94a3b8, #64748b); color: #1a1a2e; }
  .rank-bronze { background: linear-gradient(135deg, #d97706, #b45309); color: #1a1a2e; }
  .rank-num { font-weight: 600; color: var(--wp-color-text-muted); font-size: 0.875rem; }

  .player-link { color: var(--wp-color-primary); font-weight: 600; transition: color 0.2s; }
  .player-link:hover { filter: brightness(1.2); }
  .score-value { font-family: var(--wp-font-heading); font-weight: 700; font-size: 1rem; }

  .empty-state { text-align: center; padding: 5rem 1rem; }
  .empty-icon { font-size: 3rem; margin-bottom: 1rem; }
  .empty-state h3 { font-family: var(--wp-font-heading); margin-bottom: 0.5rem; }
  .empty-state-sm { padding: 2rem 1.5rem; text-align: center; }

  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); margin-bottom: 1.5rem; }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

  .site-footer { padding: 1.5rem 0; border-top: 1px solid rgba(255,255,255,0.05); }
  .footer-inner { text-align: center; }
  .copyright { font-size: 0.8125rem; color: var(--wp-color-text-muted); }

  @media (max-width: 768px) {
    .nav-links { gap: 0.75rem; }
    .nav-link-item { font-size: 0.8125rem; }
    .scores-table th, .scores-table td { padding: 0.625rem 1rem; }
  }
</style>`,
  },
  {
    name: "Player Profile",
    template: `{% theme %}
{% title "Player Profile" %}
{% favicon "favicon.svg" %}

{% section name:"nav" %}
<nav class="top-nav">
  <div class="container nav-inner">
    {% nav-link to:"/" %}<span class="brand-text">WellPlayed</span>{% end-nav-link %}
    <div class="nav-links">
      {% nav-link to:"/" %}<span class="nav-link-item">Home</span>{% end-nav-link %}
      {% nav-link to:"/tournaments" %}<span class="nav-link-item">Tournaments</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="nav-link-item">Leaderboards</span>{% end-nav-link %}
    </div>
  </div>
</nav>
{% endsection %}

{% section name:"player-profile" %}
{% capture query %}
query player($id: ID!) {
  player(id: $id) {
    id
    profiles {
      username
      firstName
      lastName
    }
    createdAt
  }
}
{% endcapture %}

{% capture variables %}
{
  "id": "{{ params.playerId }}"
}
{% endcapture %}

{% api-query query:query variables:variables %}
  {% if error %}
    <div class="container page-content">
      <div class="alert alert-error">{{ error }}</div>
    </div>
  {% endif %}

  {% if result.player %}
    <div class="profile-banner">
      <div class="banner-gradient"></div>
      <div class="container banner-content">
        <div class="profile-hero">
          <div class="profile-avatar">
            {{ result.player.profiles[0].username | slice: 0, 2 | upcase }}
          </div>
          <div class="profile-identity">
            <h1 class="profile-username">{{ result.player.profiles[0].username }}</h1>
            {% if result.player.profiles[0].firstName %}
              <p class="profile-realname">{{ result.player.profiles[0].firstName }} {{ result.player.profiles[0].lastName }}</p>
            {% endif %}
            <p class="profile-joined">Joined {{ result.player.createdAt | date: "%B %Y" }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="container page-content">
      <div class="profile-layout">
        <div class="profile-main">
          <!-- Tournament History -->
          <div class="content-block">
            <h2 class="block-title">Tournament History</h2>

            {% capture historyQuery %}
            query tournaments($query: TournamentsQuery!, $page: PageInfo!) {
              tournaments(query: $query, page: $page) {
                nodes {
                  id
                  title
                  startAt
                  endAt
                }
                totalCount
              }
            }
            {% endcapture %}

            {% capture historyVars %}
            {
              "query": {
                "orderBy": "START_AT",
                "orderDirection": "DESC",
                "status": "ALL"
              },
              "page": {
                "first": 10,
                "after": null
              }
            }
            {% endcapture %}

            {% api-query query:historyQuery variables:historyVars %}
              {% if result.tournaments.totalCount > 0 %}
                <div class="history-list">
                  {% for t in result.tournaments.nodes %}
                    {% capture tUrl %}/tournaments/{{ t.id }}{% endcapture %}
                    {% nav-link to:tUrl %}
                      <div class="history-item">
                        <div class="history-info">
                          <h4 class="history-title">{{ t.title }}</h4>
                          <span class="history-date">{{ t.startAt | date: "%b %d, %Y" }}</span>
                        </div>
                        <span class="history-arrow">&rarr;</span>
                      </div>
                    {% end-nav-link %}
                  {% endfor %}
                </div>
              {% else %}
                <div class="empty-state-sm">
                  <p class="text-muted">No tournament history to display.</p>
                </div>
              {% endif %}
            {% end-api-query %}
          </div>
        </div>

        <!-- Sidebar Stats -->
        <div class="profile-sidebar">
          <div class="sidebar-card">
            <h3 class="sidebar-title">Player Info</h3>
            <div class="stat-row">
              <span class="stat-label">Username</span>
              <span class="stat-value">{{ result.player.profiles[0].username }}</span>
            </div>
            {% if result.player.profiles[0].firstName %}
            <div class="stat-row">
              <span class="stat-label">Name</span>
              <span class="stat-value">{{ result.player.profiles[0].firstName }} {{ result.player.profiles[0].lastName }}</span>
            </div>
            {% endif %}
            <div class="stat-row">
              <span class="stat-label">Member Since</span>
              <span class="stat-value">{{ result.player.createdAt | date: "%b %Y" }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  {% endif %}
{% end-api-query %}
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; }
  body {
    font-family: var(--wp-font-family); background: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  ul { list-style: none; }
  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
  .text-muted { color: var(--wp-color-text-muted); }

  .top-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(15,15,15,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 0.875rem 0;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; }
  .brand-text { font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 800; color: var(--wp-color-primary); }
  .nav-links { display: flex; gap: 1.5rem; }
  .nav-link-item { font-size: 0.9375rem; font-weight: 500; color: var(--wp-color-text-muted); transition: color 0.2s; }
  .nav-link-item:hover { color: var(--wp-color-primary); }

  /* Banner */
  .profile-banner { position: relative; padding: 4rem 0 3rem; overflow: hidden; }
  .banner-gradient {
    position: absolute; inset: 0;
    background: linear-gradient(135deg, var(--wp-color-surface) 0%, var(--wp-color-bg) 100%);
  }
  .banner-gradient::before {
    content: ""; position: absolute; top: 0; left: 0; width: 100%; height: 100%;
    background: radial-gradient(ellipse at 20% 50%, rgba(44,177,188,0.08) 0%, transparent 60%);
  }
  .banner-content { position: relative; z-index: 1; }
  .profile-hero { display: flex; align-items: center; gap: 1.5rem; }
  .profile-avatar {
    width: 5rem; height: 5rem; border-radius: 50%; flex-shrink: 0;
    background: linear-gradient(135deg, var(--wp-color-primary), var(--wp-color-secondary));
    display: flex; align-items: center; justify-content: center;
    font-size: 1.5rem; font-weight: 800; color: #fff;
    box-shadow: 0 4px 20px rgba(44,177,188,0.25);
  }
  .profile-username { font-family: var(--wp-font-heading); font-size: 2rem; font-weight: 800; }
  .profile-realname { color: var(--wp-color-text-muted); font-size: 1rem; }
  .profile-joined { color: var(--wp-color-text-muted); font-size: 0.8125rem; margin-top: 0.25rem; }

  .page-content { padding: 2.5rem 0 5rem; }
  .profile-layout { display: grid; grid-template-columns: 1fr 320px; gap: 2rem; }

  .content-block { margin-bottom: 2rem; }
  .block-title {
    font-family: var(--wp-font-heading); font-size: 1.25rem; font-weight: 700;
    margin-bottom: 1rem; padding-bottom: 0.75rem;
    border-bottom: 2px solid var(--wp-color-primary);
  }

  .history-list { display: flex; flex-direction: column; gap: 0.5rem; }
  .history-item {
    display: flex; justify-content: space-between; align-items: center;
    background: var(--wp-color-surface); padding: 1rem 1.25rem;
    border-radius: var(--wp-border-radius); border: 1px solid rgba(255,255,255,0.05);
    transition: border-color 0.2s;
  }
  .history-item:hover { border-color: var(--wp-color-primary); }
  .history-title { font-family: var(--wp-font-heading); font-weight: 600; font-size: 0.9375rem; }
  .history-date { font-size: 0.8125rem; color: var(--wp-color-text-muted); }
  .history-arrow { color: var(--wp-color-primary); font-size: 1.125rem; }

  .profile-sidebar { display: flex; flex-direction: column; gap: 1.25rem; }
  .sidebar-card {
    background: var(--wp-color-surface); padding: 1.5rem; border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05);
  }
  .sidebar-title { font-family: var(--wp-font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 1rem; }
  .stat-row {
    display: flex; justify-content: space-between; padding: 0.625rem 0;
    border-bottom: 1px solid rgba(255,255,255,0.05); font-size: 0.875rem;
  }
  .stat-row:last-child { border-bottom: none; }
  .stat-label { color: var(--wp-color-text-muted); }
  .stat-value { font-weight: 600; }

  .empty-state-sm { padding: 2rem; text-align: center; }
  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); margin-bottom: 1.5rem; }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }

  @media (max-width: 968px) {
    .profile-layout { grid-template-columns: 1fr; }
  }
  @media (max-width: 640px) {
    .profile-hero { flex-direction: column; text-align: center; }
    .profile-username { font-size: 1.5rem; }
    .nav-links { gap: 0.75rem; }
  }
</style>`,
  },
  {
    name: "My Profile",
    template: `{% theme %}
{% title "My Profile" %}
{% favicon "favicon.svg" %}

{% section name:"nav" %}
<nav class="top-nav">
  <div class="container nav-inner">
    {% nav-link to:"/" %}<span class="brand-text">WellPlayed</span>{% end-nav-link %}
    <div class="nav-links">
      {% nav-link to:"/" %}<span class="nav-link-item">Home</span>{% end-nav-link %}
      {% nav-link to:"/tournaments" %}<span class="nav-link-item">Tournaments</span>{% end-nav-link %}
      {% nav-link to:"/leaderboards" %}<span class="nav-link-item">Leaderboards</span>{% end-nav-link %}
      {% nav-link to:"/profile" %}<span class="nav-link-item active">Profile</span>{% end-nav-link %}
    </div>
  </div>
</nav>
{% endsection %}

{% section name:"my-profile" %}
<div class="container page-content">
  {% if auth.isLoggedIn %}
    <div class="page-header">
      <h1 class="page-title">My Profile</h1>
      <p class="page-subtitle">Manage your account and preferences</p>
    </div>

    <div class="profile-layout">
      <div class="profile-main">
        <!-- Account Info -->
        <div class="content-block">
          <h2 class="block-title">Account Information</h2>
          <div class="form-card">
            {% capture updateMutation %}
            mutation updateProfile($input: UpdateProfileInput!) {
              updateProfile(input: $input) {
                id
                username
              }
            }
            {% endcapture %}

            {% form mutation:updateMutation output-variable:"profileResult" %}
              <div class="form-group">
                <label class="form-label" for="username">Username</label>
                <input type="text" id="username" name="input.username" class="form-input" placeholder="Enter your username" />
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label" for="firstName">First Name</label>
                  <input type="text" id="firstName" name="input.firstName" class="form-input" placeholder="First name" />
                </div>
                <div class="form-group">
                  <label class="form-label" for="lastName">Last Name</label>
                  <input type="text" id="lastName" name="input.lastName" class="form-input" placeholder="Last name" />
                </div>
              </div>
              <div class="form-group">
                <label class="form-label" for="email">Email</label>
                <input type="email" id="email" name="input.email" class="form-input" placeholder="your@email.com" />
              </div>
              <div class="form-actions">
                <button type="submit" class="btn btn-primary">Save Changes</button>
              </div>
            {% endform %}

            {% if profileResult %}
              {% if profileResult.error %}
                <div class="alert alert-error" style="margin-top: 1rem;">
                  {{ profileResult.error.message }}
                </div>
              {% else %}
                <div class="alert alert-success" style="margin-top: 1rem;">
                  Profile updated successfully!
                </div>
              {% endif %}
            {% endif %}
          </div>
        </div>

        <!-- My Tournaments -->
        <div class="content-block">
          <h2 class="block-title">My Tournaments</h2>

          {% capture myTournQuery %}
          query tournaments($query: TournamentsQuery!, $page: PageInfo!) {
            tournaments(query: $query, page: $page) {
              nodes {
                id
                title
                startAt
              }
              totalCount
            }
          }
          {% endcapture %}

          {% capture myTournVars %}
          {
            "query": {
              "orderBy": "START_AT",
              "orderDirection": "DESC",
              "status": "ALL"
            },
            "page": {
              "first": 5,
              "after": null
            }
          }
          {% endcapture %}

          {% api-query query:myTournQuery variables:myTournVars %}
            {% if result.tournaments.totalCount > 0 %}
              <div class="my-items-list">
                {% for t in result.tournaments.nodes %}
                  {% capture tUrl %}/tournaments/{{ t.id }}{% endcapture %}
                  {% nav-link to:tUrl %}
                    <div class="my-item">
                      <div class="my-item-info">
                        <h4 class="my-item-title">{{ t.title }}</h4>
                        <span class="my-item-date">{{ t.startAt | date: "%b %d, %Y" }}</span>
                      </div>
                      <span class="my-item-arrow">&rarr;</span>
                    </div>
                  {% end-nav-link %}
                {% endfor %}
              </div>
            {% else %}
              <div class="empty-state-sm">
                <p class="text-muted">You haven't joined any tournaments yet.</p>
                {% nav-link to:"/tournaments" %}<span class="btn btn-outline btn-sm">Browse Tournaments</span>{% end-nav-link %}
              </div>
            {% endif %}
          {% end-api-query %}
        </div>
      </div>

      <div class="profile-sidebar">
        <div class="sidebar-card">
          <h3 class="sidebar-title">Quick Links</h3>
          <div class="quick-links">
            {% nav-link to:"/tournaments" %}<span class="quick-link">Browse Tournaments</span>{% end-nav-link %}
            {% nav-link to:"/leaderboards" %}<span class="quick-link">View Leaderboards</span>{% end-nav-link %}
          </div>
        </div>
      </div>
    </div>
  {% else %}
    <div class="auth-gate">
      <div class="auth-gate-card">
        <div class="auth-icon">&#128274;</div>
        <h2 class="auth-title">Sign In Required</h2>
        <p class="auth-text">You need to sign in to view and manage your profile.</p>
        {% nav-link to:"/login" %}<span class="btn btn-primary">Sign In</span>{% end-nav-link %}
      </div>
    </div>
  {% endif %}
</div>
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; }
  body {
    font-family: var(--wp-font-family); background: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }
  ul { list-style: none; }
  .container { max-width: 1200px; margin: 0 auto; padding: 0 1.5rem; }
  .text-muted { color: var(--wp-color-text-muted); }

  .top-nav {
    position: sticky; top: 0; z-index: 100;
    background: rgba(15,15,15,0.85); backdrop-filter: blur(12px);
    border-bottom: 1px solid rgba(255,255,255,0.05); padding: 0.875rem 0;
  }
  .nav-inner { display: flex; justify-content: space-between; align-items: center; }
  .brand-text { font-family: var(--wp-font-heading); font-size: 1.375rem; font-weight: 800; color: var(--wp-color-primary); }
  .nav-links { display: flex; gap: 1.5rem; }
  .nav-link-item { font-size: 0.9375rem; font-weight: 500; color: var(--wp-color-text-muted); transition: color 0.2s; }
  .nav-link-item:hover, .nav-link-item.active { color: var(--wp-color-primary); }

  .page-content { padding: 3rem 0 5rem; }
  .page-header { margin-bottom: 2.5rem; }
  .page-title { font-family: var(--wp-font-heading); font-size: 2.5rem; font-weight: 800; }
  .page-subtitle { color: var(--wp-color-text-muted); font-size: 1.125rem; margin-top: 0.5rem; }

  .profile-layout { display: grid; grid-template-columns: 1fr 320px; gap: 2rem; }

  .content-block { margin-bottom: 2.5rem; }
  .block-title {
    font-family: var(--wp-font-heading); font-size: 1.25rem; font-weight: 700;
    margin-bottom: 1rem; padding-bottom: 0.75rem;
    border-bottom: 2px solid var(--wp-color-primary);
  }

  /* Form */
  .form-card {
    background: var(--wp-color-surface); padding: 2rem; border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05);
  }
  .form-group { margin-bottom: 1.25rem; }
  .form-row { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .form-label {
    display: block; font-size: 0.8125rem; font-weight: 600;
    color: var(--wp-color-text-muted); margin-bottom: 0.375rem;
    text-transform: uppercase; letter-spacing: 0.5px;
  }
  .form-input {
    width: 100%; padding: 0.75rem 1rem; font-size: 0.9375rem;
    background: var(--wp-color-bg); border: 1px solid rgba(255,255,255,0.1);
    border-radius: var(--wp-border-radius); color: var(--wp-color-text);
    font-family: var(--wp-font-family); transition: border-color 0.2s;
  }
  .form-input:focus { outline: none; border-color: var(--wp-color-primary); }
  .form-input::placeholder { color: var(--wp-color-text-muted); }
  .form-actions { padding-top: 0.5rem; }

  .btn {
    display: inline-block; padding: 0.75rem 1.75rem; font-weight: 600; font-size: 0.875rem;
    border-radius: var(--wp-border-radius); transition: all 0.2s; cursor: pointer;
    border: none; font-family: var(--wp-font-family);
  }
  .btn-primary { background: var(--wp-color-primary); color: var(--wp-color-bg); }
  .btn-primary:hover { filter: brightness(1.15); }
  .btn-outline { background: transparent; color: var(--wp-color-primary); border: 1px solid var(--wp-color-primary); }
  .btn-outline:hover { background: var(--wp-color-primary); color: var(--wp-color-bg); }
  .btn-sm { padding: 0.5rem 1.25rem; font-size: 0.8125rem; }

  /* My Items */
  .my-items-list { display: flex; flex-direction: column; gap: 0.5rem; }
  .my-item {
    display: flex; justify-content: space-between; align-items: center;
    background: var(--wp-color-surface); padding: 1rem 1.25rem;
    border-radius: var(--wp-border-radius); border: 1px solid rgba(255,255,255,0.05);
    transition: border-color 0.2s;
  }
  .my-item:hover { border-color: var(--wp-color-primary); }
  .my-item-title { font-family: var(--wp-font-heading); font-weight: 600; font-size: 0.9375rem; }
  .my-item-date { font-size: 0.8125rem; color: var(--wp-color-text-muted); }
  .my-item-arrow { color: var(--wp-color-primary); }

  /* Sidebar */
  .profile-sidebar { display: flex; flex-direction: column; gap: 1.25rem; }
  .sidebar-card {
    background: var(--wp-color-surface); padding: 1.5rem; border-radius: var(--wp-border-radius);
    border: 1px solid rgba(255,255,255,0.05);
  }
  .sidebar-title { font-family: var(--wp-font-heading); font-size: 1rem; font-weight: 700; margin-bottom: 1rem; }
  .quick-links { display: flex; flex-direction: column; gap: 0.5rem; }
  .quick-link {
    display: block; padding: 0.625rem 0; color: var(--wp-color-primary);
    font-weight: 500; font-size: 0.9375rem; transition: color 0.2s;
    border-bottom: 1px solid rgba(255,255,255,0.05);
  }
  .quick-link:hover { filter: brightness(1.2); }

  /* Auth Gate */
  .auth-gate { display: flex; justify-content: center; align-items: center; min-height: 60vh; }
  .auth-gate-card { text-align: center; max-width: 400px; }
  .auth-icon { font-size: 3rem; margin-bottom: 1.5rem; }
  .auth-title { font-family: var(--wp-font-heading); font-size: 2rem; font-weight: 800; margin-bottom: 0.75rem; }
  .auth-text { color: var(--wp-color-text-muted); margin-bottom: 2rem; font-size: 1.0625rem; }

  .empty-state-sm { padding: 2rem; text-align: center; }
  .empty-state-sm .btn { margin-top: 1rem; }

  .alert { padding: 1rem 1.5rem; border-radius: var(--wp-border-radius); }
  .alert-error { background: rgba(239,68,68,0.1); color: #f87171; border: 1px solid rgba(239,68,68,0.2); }
  .alert-success { background: rgba(34,197,94,0.1); color: #4ade80; border: 1px solid rgba(34,197,94,0.2); }

  @media (max-width: 968px) {
    .profile-layout { grid-template-columns: 1fr; }
  }
  @media (max-width: 640px) {
    .form-row { grid-template-columns: 1fr; }
    .nav-links { gap: 0.75rem; }
    .nav-link-item { font-size: 0.8125rem; }
  }
</style>`,
  },
  {
    name: "Login",
    template: `{% theme %}
{% title "Sign In" %}
{% favicon "favicon.svg" %}

{% section name:"login" %}
<div class="login-page">
  <div class="login-bg">
    <div class="bg-shape bg-shape-1"></div>
    <div class="bg-shape bg-shape-2"></div>
    <div class="bg-shape bg-shape-3"></div>
  </div>

  <div class="login-container">
    <div class="login-card">
      <div class="login-header">
        {% nav-link to:"/" %}<span class="login-brand">WellPlayed</span>{% end-nav-link %}
      </div>

      <div class="login-body">
        <h1 class="login-title">Welcome Back</h1>
        <p class="login-subtitle">Sign in to access your tournaments, teams, and profile.</p>

        <div class="login-methods">
          <a href="#" class="login-btn-oidc" id="oidc-login">
            <span class="login-btn-icon">&#128274;</span>
            <span>Continue with OIDC</span>
          </a>
        </div>

        <div class="login-divider">
          <span class="divider-line"></span>
          <span class="divider-text">or</span>
          <span class="divider-line"></span>
        </div>

        <div class="login-info">
          <p class="login-info-text">
            Don't have an account? Contact your tournament organizer
            or sign up through the platform's identity provider.
          </p>
        </div>

        <div class="login-features">
          <div class="feature-item">
            <span class="feature-icon">&#127942;</span>
            <div>
              <h4 class="feature-title">Join Tournaments</h4>
              <p class="feature-desc">Register and compete in organized events.</p>
            </div>
          </div>
          <div class="feature-item">
            <span class="feature-icon">&#128200;</span>
            <div>
              <h4 class="feature-title">Track Progress</h4>
              <p class="feature-desc">Monitor your rankings and match history.</p>
            </div>
          </div>
          <div class="feature-item">
            <span class="feature-icon">&#129351;</span>
            <div>
              <h4 class="feature-title">Build Your Team</h4>
              <p class="feature-desc">Create or join teams to compete together.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="login-footer">
        {% nav-link to:"/" %}<span class="back-link">&larr; Back to Home</span>{% end-nav-link %}
      </div>
    </div>
  </div>
</div>
{% endsection %}

<style>
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  html { font-size: 16px; }
  body {
    font-family: var(--wp-font-family); background: var(--wp-color-bg);
    color: var(--wp-color-text); line-height: 1.6; -webkit-font-smoothing: antialiased;
  }
  a { color: inherit; text-decoration: none; }

  .login-page {
    position: relative; min-height: 100vh; display: flex;
    align-items: center; justify-content: center; padding: 2rem;
    overflow: hidden;
  }

  /* Animated background */
  .login-bg { position: absolute; inset: 0; z-index: 0; }
  .bg-shape {
    position: absolute; border-radius: 50%; filter: blur(100px); opacity: 0.15;
  }
  .bg-shape-1 {
    width: 500px; height: 500px; top: -10%; left: -10%;
    background: var(--wp-color-primary);
    animation: float1 15s ease-in-out infinite alternate;
  }
  .bg-shape-2 {
    width: 400px; height: 400px; bottom: -5%; right: -5%;
    background: var(--wp-color-secondary);
    animation: float2 18s ease-in-out infinite alternate;
  }
  .bg-shape-3 {
    width: 300px; height: 300px; top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    background: var(--wp-color-primary);
    animation: float3 12s ease-in-out infinite alternate;
  }
  @keyframes float1 { 0% { transform: translate(0,0) scale(1); } 100% { transform: translate(5%,5%) scale(1.1); } }
  @keyframes float2 { 0% { transform: translate(0,0) scale(1); } 100% { transform: translate(-5%,-3%) scale(1.15); } }
  @keyframes float3 { 0% { transform: translate(-50%,-50%) scale(1); } 100% { transform: translate(-50%,-50%) scale(0.8); } }

  .login-container { position: relative; z-index: 1; width: 100%; max-width: 480px; }

  .login-card {
    background: var(--wp-color-surface); border-radius: calc(var(--wp-border-radius) * 2);
    border: 1px solid rgba(255,255,255,0.06); overflow: hidden;
    box-shadow: 0 20px 60px rgba(0,0,0,0.4);
  }

  .login-header {
    padding: 2rem 2rem 0; text-align: center;
  }
  .login-brand {
    font-family: var(--wp-font-heading); font-size: 1.75rem; font-weight: 800;
    color: var(--wp-color-primary);
  }

  .login-body { padding: 2rem; }
  .login-title {
    font-family: var(--wp-font-heading); font-size: 1.75rem; font-weight: 800;
    text-align: center; margin-bottom: 0.5rem;
  }
  .login-subtitle {
    text-align: center; color: var(--wp-color-text-muted); font-size: 0.9375rem;
    margin-bottom: 2rem; max-width: 340px; margin-left: auto; margin-right: auto;
  }

  .login-methods { display: flex; flex-direction: column; gap: 0.75rem; margin-bottom: 1.5rem; }
  .login-btn-oidc {
    display: flex; align-items: center; justify-content: center; gap: 0.75rem;
    padding: 0.875rem 1.5rem; border-radius: var(--wp-border-radius);
    background: var(--wp-color-primary); color: var(--wp-color-bg);
    font-weight: 600; font-size: 0.9375rem; transition: all 0.2s; cursor: pointer;
  }
  .login-btn-oidc:hover { filter: brightness(1.1); transform: translateY(-1px); }
  .login-btn-icon { font-size: 1.125rem; }

  .login-divider {
    display: flex; align-items: center; gap: 1rem; margin-bottom: 1.5rem;
  }
  .divider-line { flex: 1; height: 1px; background: rgba(255,255,255,0.08); }
  .divider-text { font-size: 0.75rem; color: var(--wp-color-text-muted); text-transform: uppercase; letter-spacing: 1px; }

  .login-info { margin-bottom: 2rem; }
  .login-info-text {
    text-align: center; font-size: 0.875rem; color: var(--wp-color-text-muted);
    max-width: 360px; margin: 0 auto;
  }

  .login-features { display: flex; flex-direction: column; gap: 1rem; }
  .feature-item {
    display: flex; align-items: flex-start; gap: 0.875rem;
    padding: 0.875rem; border-radius: var(--wp-border-radius);
    background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.04);
  }
  .feature-icon { font-size: 1.25rem; flex-shrink: 0; margin-top: 0.125rem; }
  .feature-title { font-family: var(--wp-font-heading); font-weight: 700; font-size: 0.875rem; margin-bottom: 0.125rem; }
  .feature-desc { font-size: 0.8125rem; color: var(--wp-color-text-muted); }

  .login-footer {
    padding: 1.25rem 2rem; border-top: 1px solid rgba(255,255,255,0.05);
    text-align: center;
  }
  .back-link {
    font-size: 0.875rem; color: var(--wp-color-text-muted); font-weight: 500;
    transition: color 0.2s;
  }
  .back-link:hover { color: var(--wp-color-primary); }

  @media (max-width: 520px) {
    .login-page { padding: 1rem; }
    .login-body { padding: 1.5rem; }
    .login-title { font-size: 1.5rem; }
  }
</style>`,
  },
];
